// Generates a clean HTML email from the form data for the webhook

function escapeHtml(str: any): string {
  if (str === null || str === undefined) return ''
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function renderField(label: string, value: any): string {
  if (!value || value === '') return ''
  return `<tr><td style="padding:8px 12px;color:#64748b;font-size:14px;vertical-align:top;width:200px;border-bottom:1px solid #f1f5f9;">${escapeHtml(label)}</td><td style="padding:8px 12px;font-weight:500;border-bottom:1px solid #f1f5f9;">${escapeHtml(value)}</td></tr>`
}

function renderYesNo(value: any): string {
  return value === 'true' ? 'Yes' : value === 'false' ? 'No' : ''
}

function sectionHeader(title: string): string {
  return `<tr><td colspan="2" style="padding:16px 12px 8px;"><h2 style="margin:0;font-size:18px;color:#1e293b;border-bottom:2px solid #334155;padding-bottom:6px;">${escapeHtml(title)}</h2></td></tr>`
}

function subHeader(title: string): string {
  return `<tr><td colspan="2" style="padding:12px 12px 4px;"><h3 style="margin:0;font-size:15px;color:#475569;">${escapeHtml(title)}</h3></td></tr>`
}

export function generateFormHtml(formData: any): string {
  let rows = ''

  // 1. Client Information
  rows += sectionHeader('1. Client Information')
  rows += renderField(
    'Existing Client',
    renderYesNo(formData.clientInfo?.existingClient),
  )
  if (formData.clientInfo?.existingClient === 'true') {
    rows += renderField('Which Lawyer', formData.clientInfo?.existingLawyer)
  }
  rows += renderField(
    'How did you hear about us?',
    formData.clientInfo?.referralSource,
  )
  rows += renderField('Full Legal Name', formData.clientInfo?.fullName)
  rows += renderField('Age', formData.clientInfo?.age)
  rows += renderField('Marital Status', formData.clientInfo?.maritalStatus)
  rows += renderField('Address', formData.clientInfo?.address)
  rows += renderField('Phone Number', formData.clientInfo?.phone)
  rows += renderField('Email', formData.clientInfo?.email)
  rows += renderField('Occupation', formData.clientInfo?.occupation)
  rows += renderField('Employer Name', formData.clientInfo?.employerName)
  rows += renderField('Business Address', formData.clientInfo?.businessAddress)
  rows += renderField('Business Phone', formData.clientInfo?.businessPhone)
  rows += renderField('Citizenship', formData.clientInfo?.citizenship)

  // 2. Spouse & Family
  rows += sectionHeader('2. Spouse & Family')
  if (formData.spouseInfo?.name) {
    rows += subHeader('Spouse Information')
    rows += renderField("Spouse's Name", formData.spouseInfo?.name)
    rows += renderField("Spouse's Age", formData.spouseInfo?.age)
  }
  rows += renderField(
    'Previously Married',
    renderYesNo(formData.previousMarriages?.exists),
  )
  if (formData.previousMarriages?.exists === 'true') {
    rows += renderField(
      'Ex-Spouse Name',
      formData.previousMarriages?.exSpouseName,
    )
  }
  rows += renderField(
    'Domestic Contracts',
    renderYesNo(formData.domesticContracts?.exists),
  )
  rows += renderField(
    'Support Obligations',
    renderYesNo(formData.supportObligations?.exists),
  )
  if (formData.supportObligations?.exists === 'true') {
    rows += renderField('Support Details', formData.supportObligations?.details)
  }

  if (formData.children && formData.children.length > 0) {
    rows += subHeader('Children')
    formData.children.forEach((child: any, i: number) => {
      if (child.name || child.age) {
        rows += renderField(`Child ${i + 1} Name`, child.name)
        rows += renderField(`Child ${i + 1} Age`, child.age)
      }
    })
  }

  rows += renderField(
    'Additional Persons Treated as Children',
    formData.additionalChildLikePersons,
  )

  if (formData.grandchildren && formData.grandchildren.length > 0) {
    rows += subHeader('Grandchildren')
    formData.grandchildren.forEach((gc: any, i: number) => {
      if (gc.name || gc.age) {
        rows += renderField(`Grandchild ${i + 1} Name`, gc.name)
        rows += renderField(`Grandchild ${i + 1} Age`, gc.age)
        rows += renderField(`Grandchild ${i + 1} Parent`, gc.parent)
      }
    })
  }

  rows += renderField(
    'Deceased Children',
    renderYesNo(formData.deceasedChildren?.exists),
  )
  if (formData.deceasedChildren?.exists === 'true') {
    rows += renderField(
      'Deceased Children Names',
      formData.deceasedChildren?.names,
    )
  }

  // 3. Existing Estate Documents
  rows += sectionHeader('3. Existing Estate Documents')
  rows += renderField(
    'Has Existing Will (incl. other jurisdictions)',
    renderYesNo(formData.existingDocuments?.hasWill),
  )
  if (formData.existingDocuments?.hasWill === 'true') {
    rows += renderField(
      'Will Jurisdictions',
      formData.existingDocuments?.willJurisdictions,
    )
  }

  // Primary Trustees
  if (formData.estateTrustees?.primary) {
    rows += subHeader('Primary Estate Trustee(s)')
    const trustees = Array.isArray(formData.estateTrustees.primary)
      ? formData.estateTrustees.primary
      : [formData.estateTrustees.primary]
    trustees.forEach((t: any, i: number) => {
      if (t?.name) {
        rows += renderField(`Trustee ${i + 1} Name`, t.name)
        rows += renderField(`Trustee ${i + 1} Relationship`, t.relationship)
        rows += renderField(`Trustee ${i + 1} City`, t.city)
      }
    })
  }

  // Alternate Trustees
  if (formData.estateTrustees?.alternate) {
    rows += subHeader('Alternate Estate Trustee(s)')
    const trustees = Array.isArray(formData.estateTrustees.alternate)
      ? formData.estateTrustees.alternate
      : [formData.estateTrustees.alternate]
    trustees.forEach((t: any, i: number) => {
      if (t?.name) {
        rows += renderField(`Alternate ${i + 1} Name`, t.name)
        rows += renderField(`Alternate ${i + 1} Relationship`, t.relationship)
        rows += renderField(`Alternate ${i + 1} City`, t.city)
      }
    })
  }

  // Legacy single trustee fields
  if (formData.estateTrustee?.name) {
    rows += subHeader('Estate Trustee')
    rows += renderField('Name', formData.estateTrustee?.name)
    rows += renderField('Relationship', formData.estateTrustee?.relationship)
    rows += renderField('City', formData.estateTrustee?.city)
  }
  if (formData.alternateTrustee?.name) {
    rows += subHeader('Alternate Trustee')
    rows += renderField('Name', formData.alternateTrustee?.name)
    rows += renderField('Relationship', formData.alternateTrustee?.relationship)
    rows += renderField('City', formData.alternateTrustee?.city)
  }

  // 4. Beneficiaries
  rows += sectionHeader('4. Beneficiaries')
  if (formData.specificGifts && formData.specificGifts.length > 0) {
    rows += subHeader('Specific Gifts or Bequests')
    formData.specificGifts.forEach((gift: any, i: number) => {
      if (gift.item || gift.recipient) {
        rows += renderField(`Gift ${i + 1} Item/Amount`, gift.item)
        rows += renderField(`Gift ${i + 1} Recipient`, gift.recipient)
      }
    })
  }
  rows += renderField('Distribution Type', formData.beneficiaries?.distribution)
  if (formData.beneficiaries?.distribution === 'custom') {
    rows += renderField('Custom Plan', formData.beneficiaries?.customPlan)
  }
  rows += renderField(
    'Alternate Beneficiaries',
    formData.beneficiaries?.alternates,
  )
  rows += renderField(
    'Age of Inheritance',
    formData.beneficiaries?.ageOfInheritance,
  )
  if (formData.beneficiaries?.ageOfInheritance === 'custom') {
    rows += renderField('Custom Age', formData.beneficiaries?.customAge)
  }
  rows += renderField("Guardian's Name", formData.guardians?.name)
  rows += renderField('Alternate Guardian', formData.guardians?.alternate)
  if (formData.guardians?.monthlyAllowance) {
    rows += renderField(
      'Monthly Allowance',
      `$${formData.guardians.monthlyAllowance}`,
    )
  }

  // 5. Corporate, Dependents & Special Needs
  rows += sectionHeader('5. Corporate, Dependents & Special Needs')
  rows += renderField(
    'Owns Corporation',
    renderYesNo(formData.corporateInfo?.exists),
  )
  if (formData.corporateInfo?.exists === 'true') {
    rows += renderField('Corporation Name', formData.corporateInfo?.name)
    rows += renderField('Corporation Details', formData.corporateInfo?.details)
    rows += renderField(
      'Shareholders Agreement',
      renderYesNo(formData.corporateInfo?.shareholdersAgreement),
    )
  }
  rows += renderField(
    'Dependents with Special Needs',
    renderYesNo(formData.specialNeeds?.exists),
  )
  if (formData.specialNeeds?.exists === 'true') {
    rows += renderField('Special Needs Details', formData.specialNeeds?.details)
  }
  rows += renderField('Special Considerations', formData.specialConsiderations)

  // 6. Assets & Insurance
  rows += sectionHeader('6. Assets & Insurance')
  rows += renderField(
    'Joint Ownership',
    renderYesNo(formData.jointOwnership?.exists),
  )
  if (formData.jointOwnership?.exists === 'true') {
    rows += renderField(
      'Joint Ownership Details',
      formData.jointOwnership?.details,
    )
  }

  // Registered Accounts
  rows += subHeader('Registered Accounts')
  rows += renderField('RRSP Value', formData.registeredAccounts?.rrsp?.value)
  rows += renderField(
    'RRSP Beneficiary',
    formData.registeredAccounts?.rrsp?.beneficiary,
  )
  rows += renderField('RIF Value', formData.registeredAccounts?.rif?.value)
  rows += renderField(
    'RIF Beneficiary',
    formData.registeredAccounts?.rif?.beneficiary,
  )
  rows += renderField('TFSA Value', formData.registeredAccounts?.tfsa?.value)
  rows += renderField(
    'TFSA Beneficiary',
    formData.registeredAccounts?.tfsa?.beneficiary,
  )

  rows += renderField('Has RESP', renderYesNo(formData.resp?.exists))
  if (formData.resp?.exists === 'true') {
    rows += renderField('RESP Value', formData.resp?.value)
    rows += renderField('RESP Beneficiary', formData.resp?.beneficiary)
  }

  // Real Estate
  rows += renderField(
    'Owns Real Estate',
    renderYesNo(formData.assets?.realEstate?.exists),
  )
  if (formData.assets?.realEstate?.properties) {
    const props = Array.isArray(formData.assets.realEstate.properties)
      ? formData.assets.realEstate.properties
      : [formData.assets.realEstate.properties]
    props.forEach((p: any, i: number) => {
      if (p?.address) {
        rows += subHeader(`Property ${i + 1}`)
        rows += renderField('Address', p.address)
        rows += renderField('Title Type', p.titleType)
        rows += renderField('Co-Owner Details', p.coOwnerDetails)
        rows += renderField('Value', p.value)
        rows += renderField('Mortgage Owing', p.mortgageOwing)
      }
    })
  }

  // Pensions
  rows += renderField('Pensions', formData.pensions?.details)
  rows += renderField('Pension Beneficiary', formData.pensions?.beneficiary)

  // Life Insurance
  rows += renderField(
    'Has Life Insurance',
    renderYesNo(formData.insurance?.life?.exists),
  )
  if (formData.insurance?.life?.policies) {
    const policies = Array.isArray(formData.insurance.life.policies)
      ? formData.insurance.life.policies
      : [formData.insurance.life.policies]
    policies.forEach((p: any, i: number) => {
      if (p?.insurer || p?.value) {
        rows += subHeader(`Life Insurance Policy ${i + 1}`)
        rows += renderField('Insurer', p.insurer)
        rows += renderField('Owner', p.owner)
        rows += renderField('Insured', p.insured)
        rows += renderField('Beneficiary', p.beneficiary)
        rows += renderField('Face Value', p.value)
      }
    })
  }

  // Foreign Assets
  rows += renderField(
    'Foreign Assets',
    renderYesNo(formData.foreignAssets?.exists),
  )
  if (formData.foreignAssets?.exists === 'true') {
    rows += renderField('Jurisdiction', formData.foreignAssets?.jurisdiction)
    rows += renderField('Description', formData.foreignAssets?.description)
    rows += renderField('Value', formData.foreignAssets?.value)
  }

  rows += renderField('Digital Assets', formData.assets?.digital)
  rows += renderField('Debts', formData.debts?.owed)
  rows += renderField(
    'Debts Owed to You',
    renderYesNo(formData.debts?.owedToYou?.exists),
  )
  if (formData.debts?.owedToYou?.exists === 'true') {
    rows += renderField(
      'Debts Owed to You Details',
      formData.debts?.owedToYou?.details,
    )
  }
  rows += renderField(
    'Gifts to Children for Equalization',
    renderYesNo(formData.giftsToChildren?.exists),
  )
  if (formData.giftsToChildren?.exists === 'true') {
    rows += renderField('Gifts Details', formData.giftsToChildren?.details)
  }
  rows += renderField('Other Assets', formData.assets?.other)

  // 7. POA Financial
  rows += sectionHeader('7. Power of Attorney: Financial')
  if (formData.financialPOA?.primary) {
    const attorneys = Array.isArray(formData.financialPOA.primary)
      ? formData.financialPOA.primary
      : [formData.financialPOA.primary]
    rows += subHeader('Primary Attorney(s) for Property')
    attorneys.forEach((a: any, i: number) => {
      if (a?.name) {
        rows += renderField(`Attorney ${i + 1} Name`, a.name)
        rows += renderField(`Attorney ${i + 1} Relationship`, a.relationship)
        rows += renderField(`Attorney ${i + 1} Phone`, a.phone)
        rows += renderField(`Attorney ${i + 1} Address`, a.address)
      }
    })
    if (attorneys.length > 1) {
      rows += renderField(
        'Primary Attorneys Acting',
        formData.financialPOA?.primaryActing,
      )
    }
  }
  if (formData.financialPOA?.alternate) {
    const attorneys = Array.isArray(formData.financialPOA.alternate)
      ? formData.financialPOA.alternate
      : [formData.financialPOA.alternate]
    rows += subHeader('Alternate Attorney(s) for Property')
    attorneys.forEach((a: any, i: number) => {
      if (a?.name) {
        rows += renderField(`Alternate ${i + 1} Name`, a.name)
        rows += renderField(`Alternate ${i + 1} Relationship`, a.relationship)
        rows += renderField(`Alternate ${i + 1} Phone`, a.phone)
        rows += renderField(`Alternate ${i + 1} Address`, a.address)
      }
    })
    if (attorneys.length > 1) {
      rows += renderField(
        'Alternate Attorneys Acting',
        formData.financialPOA?.alternateActing,
      )
    }
  }
  rows += renderField(
    'POA Effective Date',
    formData.financialPOA?.effectiveDate === 'uponIncapacity'
      ? 'Upon incapacity only'
      : formData.financialPOA?.effectiveDate === 'immediately'
        ? 'Effective immediately'
        : '',
  )

  // 8. POA Healthcare
  rows += sectionHeader('8. Power of Attorney: Healthcare')
  if (formData.healthcarePOA?.primary) {
    const attorneys = Array.isArray(formData.healthcarePOA.primary)
      ? formData.healthcarePOA.primary
      : [formData.healthcarePOA.primary]
    rows += subHeader('Primary Attorney(s) for Personal Care')
    attorneys.forEach((a: any, i: number) => {
      if (a?.name) {
        rows += renderField(`Attorney ${i + 1} Name`, a.name)
        rows += renderField(`Attorney ${i + 1} Relationship`, a.relationship)
        rows += renderField(`Attorney ${i + 1} Phone`, a.phone)
        rows += renderField(`Attorney ${i + 1} Address`, a.address)
      }
    })
    if (attorneys.length > 1) {
      rows += renderField(
        'Primary Attorneys Acting',
        formData.healthcarePOA?.primaryActing,
      )
    }
  }
  if (formData.healthcarePOA?.alternate) {
    const attorneys = Array.isArray(formData.healthcarePOA.alternate)
      ? formData.healthcarePOA.alternate
      : [formData.healthcarePOA.alternate]
    rows += subHeader('Alternate Attorney(s) for Personal Care')
    attorneys.forEach((a: any, i: number) => {
      if (a?.name) {
        rows += renderField(`Alternate ${i + 1} Name`, a.name)
        rows += renderField(`Alternate ${i + 1} Relationship`, a.relationship)
        rows += renderField(`Alternate ${i + 1} Phone`, a.phone)
        rows += renderField(`Alternate ${i + 1} Address`, a.address)
      }
    })
    if (attorneys.length > 1) {
      rows += renderField(
        'Alternate Attorneys Acting',
        formData.healthcarePOA?.alternateActing,
      )
    }
  }

  const clientName = formData.clientInfo?.fullName || 'Unknown'
  const submittedDate = new Date().toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Estate Planning Intake Form – ${escapeHtml(clientName)}</title>
</head>
<body style="margin:0;padding:0;background:#f8fafc;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1e293b;font-size:15px;line-height:1.6;">
  <div style="max-width:720px;margin:0 auto;padding:24px;">
    <div style="background:#1e293b;color:#fff;padding:24px 28px;border-radius:8px 8px 0 0;">
      <h1 style="margin:0;font-size:22px;font-weight:600;">Estate Planning Intake Form</h1>
      <p style="margin:6px 0 0;color:#94a3b8;font-size:14px;">Will and Power of Attorney Questionnaire</p>
    </div>
    <div style="background:#fff;padding:24px 28px;border:1px solid #e2e8f0;border-top:none;border-radius:0 0 8px 8px;">
      <div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:6px;padding:12px 16px;margin-bottom:20px;">
        <p style="margin:0;font-size:14px;color:#1e40af;"><strong>Client:</strong> ${escapeHtml(clientName)}</p>
        <p style="margin:4px 0 0;font-size:14px;color:#1e40af;"><strong>Submitted:</strong> ${escapeHtml(submittedDate)}</p>
      </div>
      <table style="width:100%;border-collapse:collapse;">
        ${rows}
      </table>
    </div>
    <p style="text-align:center;color:#94a3b8;font-size:12px;margin-top:16px;">
      This form was submitted electronically via the Estate Planning Intake Form.
    </p>
  </div>
</body>
</html>`
}
